import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { reducedMotion } from '../../hooks/useLenis.js';
import './PageCurtain.css';

const COVER_MS = 520;

export const PageCurtain = () => {
  const curtainRef = useRef(null);
  const markRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isReduced = reducedMotion();

  useEffect(() => {
    if (isReduced) return;

    const curtain = curtainRef.current;
    const mark = markRef.current;
    if (!curtain || !mark) return;

    const reveal = () => {
      curtain.dataset.state = 'revealing';
      gsap.set(curtain, { yPercent: 0 });
      gsap.set(mark, { opacity: 1 });

      const tl = gsap.timeline({
        onComplete: () => {
          curtain.dataset.state = 'idle';
        },
      });
      tl.to(mark, { opacity: 0, duration: 0.25, ease: 'power2.in' }).to(
        curtain,
        { yPercent: -100, duration: 0.7, ease: 'power3.inOut' },
        '-=0.05'
      );
    };

    reveal();

    const handleClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      const link = target?.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || link.target === '_blank' || link.hasAttribute('download')) return;
      if (/^(https?:|mailto:|tel:|#)/.test(href)) return;

      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;

      event.preventDefault();

      let navigated = false;
      const go = () => {
        if (navigated) return;
        navigated = true;
        window.location.href = url.href;
      };

      curtain.dataset.state = 'covering';
      gsap.set(curtain, { yPercent: 100 });
      gsap.to(curtain, {
        yPercent: 0,
        duration: COVER_MS / 1000,
        ease: 'power3.inOut',
        onComplete: go,
      });
      gsap.fromTo(
        mark,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, delay: 0.18, ease: 'power2.out' }
      );

      window.setTimeout(go, COVER_MS + 220);
    };

    document.addEventListener('click', handleClick);

    const onPageShow = (event) => {
      if (event.persisted) reveal();
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [isReduced, navigate]);

  useEffect(() => {
    if (isReduced) return;
    const curtain = curtainRef.current;
    const mark = markRef.current;
    if (!curtain || !mark) return;

    curtain.dataset.state = 'revealing';
    gsap.set(curtain, { yPercent: 0 });
    gsap.set(mark, { opacity: 1 });

    const tl = gsap.timeline({
      onComplete: () => {
        curtain.dataset.state = 'idle';
      },
    });
    tl.to(mark, { opacity: 0, duration: 0.25, ease: 'power2.in' }).to(
      curtain,
      { yPercent: -100, duration: 0.7, ease: 'power3.inOut' },
      '-=0.05'
    );
  }, [location.pathname, isReduced]);

  if (isReduced) {
    return null;
  }

  return (
    <div
      ref={curtainRef}
      className="curtain"
      aria-hidden="true"
      data-state="idle"
    >
      <div ref={markRef} className="curtain-mark">
        <img
          src="/media/logo_background_remove.png"
          alt="ASTRID FIRST"
          className="curtain-logo"
        />
      </div>
    </div>
  );
};